// Tracks the user's previous-session timestamp so we can flag items added since they last looked.
// "Session" here is generous — any visit older than 30 minutes counts as a new session, so a
// quick page refresh doesn't blow away the NEW markers the user is still actively scanning.

const KEY_LAST_VISIT = "vault.lastVisit.v1";       // unix-ms of the start of the PREVIOUS session
const KEY_CURRENT_VISIT = "vault.currentVisit.v1"; // unix-ms of when the CURRENT session began
const SESSION_GAP_MS = 30 * 60 * 1000;             // 30 min idle = treat next visit as a new session

/**
 * Returns the timestamp the user's previous session began (ms since epoch), or null on first visit.
 * Stable across the current session — won't shift while they're clicking around.
 */
export function getLastVisit() {
  try {
    const v = localStorage.getItem(KEY_LAST_VISIT);
    return v ? Number(v) : null;
  } catch {
    return null;
  }
}

/**
 * Call on every app mount. If the existing "current" session is older than SESSION_GAP_MS we treat
 * this as a fresh visit: the old "current" becomes the new "last", and we stamp a new "current".
 * Otherwise we just bump the "current" so the gap-check keeps working while they're active.
 * The lastVisit returned for badge comparison is the value present at the START of this session.
 */
export function markVisitNow() {
  try {
    const now = Date.now();
    const prevCurrent = Number(localStorage.getItem(KEY_CURRENT_VISIT) || 0);
    if (!prevCurrent || (now - prevCurrent) > SESSION_GAP_MS) {
      // Fresh session — roll prevCurrent into lastVisit so badges anchor to that timestamp.
      if (prevCurrent) localStorage.setItem(KEY_LAST_VISIT, String(prevCurrent));
      localStorage.setItem(KEY_CURRENT_VISIT, String(now));
    } else {
      // Still in the same session — just keep currentVisit warm.
      localStorage.setItem(KEY_CURRENT_VISIT, String(now));
    }
  } catch { /* private mode etc. — feature degrades silently */ }
}

/**
 * Was this item added after the user's previous session began?
 * `createdAt` may be ISO string, Date, or unix-ms number — all handled.
 * Returns false if there's no previous visit recorded (so first-time users don't see EVERYTHING tagged NEW).
 */
export function isNewSinceLastVisit(createdAt) {
  if (!createdAt) return false;
  const last = getLastVisit();
  if (!last) return false;
  let t;
  try {
    if (typeof createdAt === "number") t = createdAt;
    else if (createdAt instanceof Date) t = createdAt.getTime();
    else t = new Date(createdAt).getTime();
  } catch {
    return false;
  }
  if (!Number.isFinite(t)) return false;
  return t > last;
}
